import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  Lock as LockIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  VideoLibrary as VideoLibraryIcon,
  Article as ArticleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import LessonCompletionModal from './LessonCompletionModal';
import LessonStats from './LessonStats';
import { useLessonProgress } from '../../hooks/useLessonProgress';

const LessonsSection = ({
  module,
  isEnrolled,
  userProgress,
  onStartLesson,
  onStartTest,
  onViewTest,
  hasSpecialAccess = false,
  className,
}) => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation(['courses', 'common']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [viewMode, setViewMode] = useState('list');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);

  // На мобильных устройствах всегда используем grid view
  const effectiveViewMode = isMobile ? 'grid' : viewMode;

  // Используем хук для работы с прогрессом уроков
  const {
    getItemStatus,
    getTestProgressInfo,
    getCardStyles,
    getIconBackgroundColor,
    getIconGradient,
    getIconColor,
    formatDuration,
  } = useLessonProgress(userProgress, t);

  const handleLessonCardClick = useCallback(
    (item, itemIndex) => {
      if (!isAuthenticated) {
        toast.info(t('courses:courseDetail.toasts.loginToEnroll'));
        return;
      }

      const lessonId =
        typeof item._id === 'string' ? item._id : item._id.toString();
      const isCompleted = userProgress?.completedLessons?.includes(lessonId);

      if (isCompleted) {
        // Если урок завершен и у него есть тесты - показываем модальное окно
        if (item.tests && Array.isArray(item.tests) && item.tests.length > 0) {
          setSelectedLesson(item);
          setModalOpen(true);
        } else {
          // Если тестов нет - сразу открываем урок
          if (onStartLesson) {
            onStartLesson(itemIndex);
          }
        }
      } else {
        if (onStartLesson) {
          onStartLesson(itemIndex);
        }
      }
    },
    [isAuthenticated, onStartLesson, userProgress, t]
  );

  // Close modal
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedLesson(null);
  }, []);

  // Handle repeat lesson from modal
  const handleRepeatLesson = useCallback(() => {
    if (selectedLesson && onStartLesson) {
      const itemIndex = module?.lessons.findIndex(
        (item) => item._id === selectedLesson._id
      );
      if (itemIndex !== -1) {
        onStartLesson(itemIndex);
        handleCloseModal();
      }
    }
  }, [selectedLesson, onStartLesson, handleCloseModal, module?.lessons]);

  // Handle start test from modal
  const handleStartTestFromModal = useCallback(
    (testId) => {
      if (testId && onStartTest) {
        const moduleData = module.module || module;
        const lessonSlug = selectedLesson?.slug || selectedLesson?._id;
        onStartTest(testId, moduleData.slug || moduleData._id, lessonSlug);
        handleCloseModal();
      }
    },
    [onStartTest, module, selectedLesson, handleCloseModal]
  );

  // Handle view test from modal
  const handleViewTestFromModal = useCallback(
    (testId) => {
      if (testId && onViewTest) {
        const moduleData = module.module || module;
        onViewTest(testId, moduleData.slug || moduleData._id);
        handleCloseModal();
      }
    },
    [onViewTest, module, handleCloseModal]
  );

  // Мемоизируем данные для каждого урока
  const lessonsData = useMemo(() => {
    if (!module?.lessons) return [];

    return module.lessons.map((item, index) => {
      const status = getItemStatus(item, module);
      const testProgressInfo = getTestProgressInfo(item);

      return {
        ...item,
        index,
        status,
        testProgressInfo,
        cardStyles: getCardStyles(status, theme),
        iconBackgroundColor: getIconBackgroundColor(status),
        iconGradient: getIconGradient(status),
        iconColor: getIconColor(status),
      };
    });
  }, [
    module,
    getItemStatus,
    getTestProgressInfo,
    getCardStyles,
    theme,
    getIconBackgroundColor,
    getIconGradient,
    getIconColor,
  ]);

  const renderItemListItem = useCallback(
    (lessonData) => {
      const {
        status,
        cardStyles,
        iconBackgroundColor,
        iconGradient,
        iconColor,
        testProgressInfo,
      } = lessonData;

      return (
        <Paper
          key={lessonData.index}
          sx={{
            ...cardStyles,
            p: 3,
            cursor: status === 'locked' ? 'not-allowed' : 'pointer',
            '&:hover':
              status !== 'locked'
                ? {
                    boxShadow: 3,
                  }
                : {},
          }}
          onClick={() => handleLessonCardClick(lessonData, lessonData.index)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {/* Item Thumbnail */}
            <Box
              sx={{
                position: 'relative',
                width: 180,
                height: 180,
                flexShrink: 0,
              }}
            >
              {lessonData.image ? (
                <Box
                  component="img"
                  src={lessonData.image}
                  alt={lessonData.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'right center',
                    borderRadius: 2,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 2,
                    backgroundColor: iconBackgroundColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Background Pattern */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: iconGradient,
                    }}
                  />

                  {/* Icon */}
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    {lessonData.type === 'video' ||
                    lessonData.contentType === 'video' ? (
                      <VideoLibraryIcon
                        sx={{
                          fontSize: 48,
                          color: iconColor,
                        }}
                      />
                    ) : (
                      <ArticleIcon
                        sx={{
                          fontSize: 48,
                          color: iconColor,
                        }}
                      />
                    )}
                  </Box>

                  {/* Lock overlay for locked items */}
                  {status === 'locked' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                      }}
                    >
                      <LockIcon sx={{ fontSize: 32, color: 'white' }} />
                    </Box>
                  )}
                </Box>
              )}

              {/* Duration Badge */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  fontSize: '0.75rem',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                {formatDuration(lessonData.duration)}
              </Box>
            </Box>

            {/* Content and Action Container */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                flex: '1 1 320px',
                minWidth: 0,
              }}
            >
              {/* Item Content */}
              <Box
                sx={{
                  flex: '1 1 60%',
                  minWidth: 0,
                }}
              >
                {/* Title and Status */}
                <Typography
                  variant="h6"
                  component="h4"
                  sx={{
                    fontWeight: 600,
                    color:
                      status === 'locked' ? 'text.disabled' : 'text.primary',
                    lineHeight: 1.3,
                    mb: 1,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {lessonData.title}
                </Typography>

                {/* Description */}
                {lessonData.isTest && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      lineHeight: 1.5,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {lessonData.description ||
                      (lessonData.content
                        ? lessonData.content.length > 150
                          ? lessonData.content.substring(0, 150) + '...'
                          : lessonData.content
                        : '')}
                  </Typography>
                )}

                {/* Stats */}
                <LessonStats
                  lesson={lessonData}
                  testProgressInfo={testProgressInfo}
                  variant="compact"
                />

                {/* Status Indicator */}
                {status !== 'available' && (
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {(status === 'test_passed' || status === 'completed') && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label={t('common:status.completed')}
                        color="success"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {status === 'test_failed' && (
                      <Chip
                        icon={<CancelIcon />}
                        label={t('common:status.testFailed')}
                        color="error"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {status === 'locked' && (
                      <Chip
                        icon={<LockIcon />}
                        label={t('courses:courseDetail.lessonStatus.locked')}
                        disabled
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      );
    },
    [handleLessonCardClick, formatDuration, t]
  );

  const renderItemCard = useCallback(
    (lessonData) => {
      const {
        status,
        cardStyles,
        iconBackgroundColor,
        iconGradient,
        iconColor,
        testProgressInfo,
      } = lessonData;

      return (
        <Card
          key={lessonData.index}
          sx={{
            ...cardStyles,
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            cursor: status === 'locked' ? 'not-allowed' : 'pointer',
            '&:hover':
              status !== 'locked'
                ? {
                    boxShadow: 3,
                  }
                : {},
          }}
          onClick={() => handleLessonCardClick(lessonData, lessonData.index)}
        >
          {/* Item Thumbnail/Icon */}
          <Box
            sx={{
              position: 'relative',
              aspectRatio: '1',
              overflow: 'hidden',
            }}
          >
            {lessonData.image ? (
              <Box
                component="img"
                src={lessonData.image}
                alt={lessonData.title}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'right center',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: iconBackgroundColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                {/* Background Pattern */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: iconGradient,
                  }}
                />

                {/* Icon */}
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  {lessonData.type === 'video' ||
                  lessonData.contentType === 'video' ? (
                    <VideoLibraryIcon
                      sx={{
                        fontSize: 48,
                        color: iconColor,
                      }}
                    />
                  ) : (
                    <ArticleIcon
                      sx={{
                        fontSize: 48,
                        color: iconColor,
                      }}
                    />
                  )}
                </Box>

                {/* Lock overlay for locked items */}
                {status === 'locked' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2,
                    }}
                  >
                    <LockIcon sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                )}
              </Box>
            )}

            {/* Duration Badge */}
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                fontSize: '0.75rem',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              {formatDuration(lessonData.duration)}
            </Box>
          </Box>

          <CardContent
            sx={{
              p: 2,
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            {/* Item Title */}
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontWeight: 600,
                color: status === 'locked' ? 'text.disabled' : 'text.primary',
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mb: 1,
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              {lessonData.title}
            </Typography>

            {/* Stats */}
            <LessonStats
              lesson={lessonData}
              testProgressInfo={testProgressInfo}
              variant="compact"
            />

            {/* Status Indicator */}
            <Box sx={{ mt: 'auto' }}>
              {(status === 'test_passed' || status === 'completed') && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label={t('common:status.completed')}
                  color="success"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {status === 'test_failed' && (
                <Chip
                  icon={<CancelIcon />}
                  label={t('common:status.testFailed')}
                  color="error"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {status === 'locked' && (
                <Chip
                  icon={<LockIcon />}
                  label={t('courses:courseDetail.lessonStatus.locked')}
                  disabled
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      );
    },
    [handleLessonCardClick, formatDuration, t]
  );

  if (!module) {
    return null;
  }

  return (
    <Box
      id="lessons-section"
      className={className}
      sx={{
        mt: 6,
        scrollMarginTop: '80px',
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography
          variant="h5"
          component="h3"
          fontWeight="bold"
          sx={{
            color: 'text.primary',
          }}
        >
          {t('courses:courseDetail.lessonsAndTests')}
        </Typography>

        {/* View Mode Toggle - скрыт на мобильных устройствах */}
        {!isMobile && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(event, newMode) => {
              if (newMode !== null) {
                setViewMode(newMode);
              }
            }}
            aria-label="view mode"
            sx={{
              gap: 1,
              '& .MuiToggleButtonGroup-grouped': {
                border: 1,
                borderRadius: 2,
                '&:not(:first-of-type)': {
                  marginLeft: 0,
                },
              },
              '& .MuiToggleButton-root': {
                px: 1.5,
                py: 1,
                fontSize: '0.875rem',
                fontWeight: 400,
                textTransform: 'none',
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.secondary',
                backgroundColor: 'background.paper',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderColor: 'text.disabled',
                },
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    borderColor: 'primary.dark',
                  },
                },
              },
            }}
          >
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon sx={{ mr: 0.5, fontSize: 16 }} />
              {t('common:view.grid')}
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon sx={{ mr: 0.5, fontSize: 16 }} />
              {t('common:view.list')}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Items Content */}
      {lessonsData.length > 0 ? (
        effectiveViewMode === 'list' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {lessonsData.map(renderItemListItem)}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 3,
              maxWidth: '100%',
            }}
          >
            {lessonsData.map(renderItemCard)}
          </Box>
        )
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <VideoLibraryIcon
            sx={{
              fontSize: 64,
              color: 'text.disabled',
              mb: 2,
            }}
          />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            {t('courses:courseDetail.noLessons')}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {t('courses:courseDetail.noLessonsDesc')}
          </Typography>
        </Paper>
      )}

      {/* Lesson Completion Modal */}
      <LessonCompletionModal
        open={modalOpen}
        onClose={handleCloseModal}
        lesson={selectedLesson}
        tests={selectedLesson?.tests || []}
        userProgress={userProgress}
        onRepeatLesson={handleRepeatLesson}
        onStartTest={handleStartTestFromModal}
        onViewTest={handleViewTestFromModal}
        hasSpecialAccess={hasSpecialAccess}
      />
    </Box>
  );
};

export default LessonsSection;
